import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import translations from './translations';
import { createT } from './i18n/translate';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const STORAGE_KEY = 'zr_language';
const SUPPORTED = ['sl', 'en'];

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3004';

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(saved) ? saved : 'sl';
  });

  // Admin-edited overrides, shape: { sl: {...}, en: {...} }
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  // Non-blocking: defaults render immediately, overrides swap in on arrival.
  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/translations`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data) setOverrides(data);
      })
      .catch(() => {
        // Non-fatal: the app renders correctly from the bundled defaults.
      });
    return () => {
      active = false;
    };
  }, []);

  const setLanguage = (next) => {
    if (SUPPORTED.includes(next)) setLanguageState(next);
  };

  const toggleLanguage = () => {
    setLanguageState((prev) => (prev === 'sl' ? 'en' : 'sl'));
  };

  const t = useMemo(
    () => createT({ translations, overrides, language }),
    [overrides, language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, toggleLanguage, t }),
    [language, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageContext;
